import { createJSTProctor } from "@indeed/tare/proctor/JSTProctor";
import { Platform } from "react-native";

import { RawNativeModules } from "../internal/TypedNativeModules";
import type { TareBridge } from "../types/TareBridge";
import { isTareCodegenActive, isTareCodegenActiveSync } from "./tareCodegenMigration";

// Preserve bridge identity for Suspense readiness; model values are never cached here.
const modelBridges = new WeakMap<TareBridge, TareBridge>();

/**
 * Native host defaults route model reads through the codegen rollout gate.
 * Web and explicitly injected bridges retain their own implementations.
 */
export function getTareBridge(): TareBridge | null {
  const bridge = RawNativeModules.RNTareBridge;
  if (!bridge || Platform.OS === "web") return bridge ?? null;
  const existing = modelBridges.get(bridge);
  if (existing) return existing;

  const proctor = createJSTProctor(() => bridge);
  const modelMethods: TareBridge = {
    async getAccountStatus() {
      if (!(await isTareCodegenActive(proctor))) return bridge.getAccountStatus?.() ?? null;
      try {
        return RawNativeModules.NativeJSTAccountStatus?.getAccountStatus() ?? null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    getAccountStatusSync() {
      if (!isTareCodegenActiveSync(proctor)) return bridge.getAccountStatusSync?.() ?? null;
      try {
        return RawNativeModules.NativeJSTAccountStatus?.getAccountStatus() ?? null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async getAppModel() {
      if (!(await isTareCodegenActive(proctor))) return bridge.getAppModel?.() ?? null;
      try {
        return (await RawNativeModules.NativeJSTAppModel?.getAppModel()) ?? null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    getAppModelSync() {
      if (!isTareCodegenActiveSync(proctor)) return bridge.getAppModelSync?.() ?? null;
      try {
        return RawNativeModules.NativeJSTAppModel?.getAppModelSync() ?? null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async getAppSessionInfo() {
      if (!(await isTareCodegenActive(proctor))) return bridge.getAppSessionInfo?.() ?? null;
      try {
        return RawNativeModules.NativeJSTAppSessionInfo?.getAppSessionInfo() ?? null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    getAppSessionInfoSync() {
      if (!isTareCodegenActiveSync(proctor)) return bridge.getAppSessionInfoSync?.() ?? null;
      try {
        return RawNativeModules.NativeJSTAppSessionInfo?.getAppSessionInfo() ?? null;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
  };
  // Bridgeless modules expose uncached methods on a native prototype; spreading drops them.
  const modelBridge = new Proxy(bridge, {
    get(target, property): unknown {
      return Object.hasOwn(modelMethods, property)
        ? Reflect.get(modelMethods, property)
        : Reflect.get(target, property);
    },
  });
  modelBridges.set(bridge, modelBridge);
  return modelBridge;
}
